import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ReportProblem from '@mui/icons-material/esm/ReportProblem';
import BugReport from '@mui/icons-material/esm/BugReport';
import Build from '@mui/icons-material/esm/Build';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import Cancel from '@mui/icons-material/esm/Cancel';
import { useSelector } from 'react-redux';

import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import defectService, {
  DefectBoardRow,
  DefectsBoard,
  DefectStatusFilter,
  DefectSeverityFilter,
} from '../../services/api/defectService';
import { cachedDefectsBoard, mergeById } from '../../services/offline/fieldOfflineForms';
import DefectAnalyticsPage from './DefectAnalyticsPage';

const STATUS_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  open: 'error',
  assigned: 'warning',
  fixing: 'warning',
  recheck_pending: 'info',
  closed_passed: 'success',
  closed_disputed: 'error',
  void: 'default',
};

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  assigned: 'Assigned',
  fixing: 'Fixing',
  recheck_pending: 'Recheck',
  closed_passed: 'Closed — Passed',
  closed_disputed: 'Closed — Disputed',
  void: 'Voided',
};

const SEVERITY_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  low: 'default',
  medium: 'primary',
  high: 'warning',
  critical: 'error',
};

interface DefectsPageProps {
  fieldMode?: boolean;
}

export default function DefectsPage({ fieldMode = false }: DefectsPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const initialTab = new URLSearchParams(location.search).get('tab') === 'analytics' ? 'analytics' : 'register';
  const [tab, setTab] = useState<'register' | 'analytics'>(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DefectsBoard | null>(null);
  const [statusFilter, setStatusFilter] = useState<DefectStatusFilter>('all');
  const [severityFilter, setSeverityFilter] = useState<DefectSeverityFilter>('all');
  const [activeOnly, setActiveOnly] = useState<boolean>(false);
  const [selected, setSelected] = useState<DefectBoardRow | null>(null);

  useEffect(() => {
    if (!activeProjectId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const cached = fieldMode
          ? await cachedDefectsBoard(activeProjectId, { status: statusFilter, severity: severityFilter, activeOnly })
          : null;
        if (fieldMode && !navigator.onLine) {
          if (!cancelled) setData(cached);
          return;
        }
        const result = await defectService.getDefectsBoard(activeProjectId, {
          status: statusFilter,
          severity: severityFilter,
          activeOnly,
        });
        if (!cancelled) setData(cached ? mergeDefectsBoard(result, cached) : result);
      } catch (err: any) {
        if (fieldMode) {
          const cached = await cachedDefectsBoard(activeProjectId, { status: statusFilter, severity: severityFilter, activeOnly });
          if (!cancelled) {
            setData(cached);
            setError(cached.defects.length ? null : err.response?.data?.message || 'Failed to load defects');
          }
        } else if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load defects');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeProjectId, statusFilter, severityFilter, activeOnly]);

  const filtered = useMemo(() => data?.defects ?? [], [data]);

  useEffect(() => {
    setTab(new URLSearchParams(location.search).get('tab') === 'analytics' ? 'analytics' : 'register');
  }, [location.search]);

  if (!activeProjectId) {
    return (
      <Box>
        <PageHeader title="Defects / Remedial Work" />
        <Alert severity="info">Select a project to view defects.</Alert>
      </Box>
    );
  }

  const columns: Column<DefectBoardRow>[] = [
    {
      id: 'severity',
      label: 'Severity',
      minWidth: 90,
      format: (value: string) => (
        <Chip size="small" label={value.toUpperCase()} color={SEVERITY_COLOR[value] || 'default'} />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 130,
      format: (value: string) => (
        <Chip size="small" label={STATUS_LABEL[value] || value} color={STATUS_COLOR[value] || 'default'} />
      ),
    },
    { id: 'title', label: 'Title', minWidth: 220 },
    {
      id: 'unitCode',
      label: 'Unit',
      minWidth: 80,
      format: (value: string | null) => value ?? '—',
    },
    {
      id: 'originalTaskCode',
      label: 'Task',
      minWidth: 110,
    },
    {
      id: 'assignedToTeamName',
      label: 'Team',
      minWidth: 140,
      format: (value: string | null) => value ?? '—',
    },
    {
      id: 'reworkCount',
      label: 'Rework',
      minWidth: 80,
      align: 'right',
      format: (value: number) => (value > 0 ? (
        <Typography variant="body2" fontWeight="bold" color="warning.main">{value}</Typography>
      ) : '—'),
    },
    {
      id: 'ageDays',
      label: 'Age (days)',
      minWidth: 90,
      align: 'right',
      format: (value: number, row: DefectBoardRow) => (
        <Typography
          variant="body2"
          color={!row.closedAt && value > 14 ? 'error.main' : 'text.primary'}
          fontWeight={!row.closedAt && value > 14 ? 'bold' : 'normal'}
        >
          {value}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Defects / Remedial Work"
        subtitle={data ? `${data.totals.count} defect${data.totals.count === 1 ? '' : 's'} (filtered view)` : undefined}
      />

      {!fieldMode && (
        <Paper sx={{ mb: 2, borderRadius: 1 }}>
          <Tabs
            value={tab}
            onChange={(_, value: 'register' | 'analytics') => {
              setTab(value);
              navigate(value === 'analytics' ? '/defects?tab=analytics' : '/defects', { replace: true });
            }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="register" label="Register" />
            <Tab value="analytics" label="Analytics" />
          </Tabs>
        </Paper>
      )}

      {!fieldMode && tab === 'analytics' ? (
        <DefectAnalyticsPage embedded />
      ) : (
        <>

      {/* Summary KPIs */}
      {data && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <KPICard title="Open" value={`${data.totals.open}`} icon={<ReportProblem />} color={data.totals.open > 0 ? 'error' : 'success'} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KPICard title="In Progress" value={`${data.totals.inProgress}`} icon={<Build />} color={data.totals.inProgress > 0 ? 'warning' : 'success'} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KPICard title="Awaiting Recheck" value={`${data.totals.awaitingRecheck}`} icon={<BugReport />} color={data.totals.awaitingRecheck > 0 ? 'info' : 'success'} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KPICard title="Closed — Passed" value={`${data.totals.closedPassed}`} icon={<CheckCircle />} color="success" />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KPICard title="Closed — Disputed" value={`${data.totals.closedDisputed}`} icon={<Cancel />} color={data.totals.closedDisputed > 0 ? 'error' : 'success'} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KPICard
              title="Critical / High"
              value={`${data.totals.bySeverity.critical + data.totals.bySeverity.high}`}
              changeLabel={`${data.totals.bySeverity.critical} critical · ${data.totals.bySeverity.high} high`}
              icon={<ReportProblem />}
              color={data.totals.bySeverity.critical > 0 ? 'error' : data.totals.bySeverity.high > 0 ? 'warning' : 'success'}
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            inputProps={{ 'aria-label': 'Defect status filter' }}
            onChange={(e) => setStatusFilter(e.target.value as DefectStatusFilter)}
          >
            <MenuItem value="all">All statuses</MenuItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            value={severityFilter}
            label="Severity"
            inputProps={{ 'aria-label': 'Defect severity filter' }}
            onChange={(e) => setSeverityFilter(e.target.value as DefectSeverityFilter)}
          >
            <MenuItem value="all">All severities</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />}
          label="Active only (open/assigned/fixing/recheck)"
        />
      </Box>

      {/* Body */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 200, alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !data || filtered.length === 0 ? (
        <Alert severity="info">No defects match the current filters.</Alert>
      ) : (
        <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <DataTable
            columns={columns}
            data={filtered}
            pagination
            searchable
            onRowClick={(row) => setSelected(row)}
          />
        </Box>
      )}

      {/* Detail drawer */}
      <Drawer anchor="right" open={!!selected} onClose={() => setSelected(null)}>
        {selected && (
          <Box sx={{ width: 400, p: 3 }}>
            <Typography variant="h6" fontWeight="bold">{selected.title}</Typography>
            <Box sx={{ mt: 1, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label={STATUS_LABEL[selected.status] || selected.status} color={STATUS_COLOR[selected.status] || 'default'} />
              <Chip size="small" label={selected.severity.toUpperCase()} color={SEVERITY_COLOR[selected.severity] || 'default'} />
              {selected.defectType && <Chip size="small" label={selected.defectType} variant="outlined" />}
            </Box>
            <Divider />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Original task</Typography>
              <Typography variant="body2"><strong>{selected.originalTaskCode}</strong> — {selected.originalTaskName}</Typography>
              <Typography variant="body2">Rework count: {selected.reworkCount}</Typography>
              {selected.remedialTaskCode && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Remedial task: <strong>{selected.remedialTaskCode}</strong> ({selected.remedialTaskStatus})
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2">Location + responsibility</Typography>
              <Typography variant="body2">Unit: {selected.unitCode ? `${selected.unitCode} — ${selected.unitName}` : '—'}</Typography>
              <Typography variant="body2">Assigned team: {selected.assignedToTeamName ?? '—'}</Typography>
              <Typography variant="body2">Raised by: {selected.raisedByName ?? '—'}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2">Timeline</Typography>
              <Typography variant="body2">Created: {new Date(selected.createdAt).toLocaleString()}</Typography>
              {selected.closedAt && (
                <Typography variant="body2">Closed: {new Date(selected.closedAt).toLocaleString()}</Typography>
              )}
              <Typography variant="body2">Age: <strong>{selected.ageDays} day{selected.ageDays === 1 ? '' : 's'}</strong></Typography>
            </Box>
            {selected.description && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2">Description</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selected.description}</Typography>
                </Box>
              </>
            )}
            {selected.resolutionNotes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2">Resolution notes</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selected.resolutionNotes}</Typography>
                </Box>
              </>
            )}
          </Box>
        )}
      </Drawer>
        </>
      )}
    </Box>
  );
}

function mergeDefectsBoard(server: DefectsBoard, cached: DefectsBoard): DefectsBoard {
  return buildDefectsBoard(mergeById(server.defects, cached.defects));
}

function buildDefectsBoard(rows: DefectBoardRow[]): DefectsBoard {
  return {
    defects: rows,
    totals: {
      count: rows.length,
      open: rows.filter((row) => row.status === 'open').length,
      inProgress: rows.filter((row) => ['assigned', 'fixing'].includes(row.status)).length,
      awaitingRecheck: rows.filter((row) => row.status === 'recheck_pending').length,
      closedPassed: rows.filter((row) => row.status === 'closed_passed').length,
      closedDisputed: rows.filter((row) => row.status === 'closed_disputed').length,
      voided: rows.filter((row) => row.status === 'void').length,
      bySeverity: {
        low: rows.filter((row) => row.severity === 'low').length,
        medium: rows.filter((row) => row.severity === 'medium').length,
        high: rows.filter((row) => row.severity === 'high').length,
        critical: rows.filter((row) => row.severity === 'critical').length,
      },
    },
  };
}
